import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import KfLogo from '../../../assets/kf-logo-rosasymbol-svart-cmyk.png'
import FellixSemiBoldFontUrl from '../../../fonts/fellix/Fellix-SemiBold.ttf';
import FellixRegularFontUrl from '../../../fonts/fellix/Fellix-Regular.ttf';
import FellixBoldFontUrl from '../../../fonts/fellix/Fellix-Bold.ttf';
import { expandableNæringConfig, expandablePrivatConfig, privatConfig, næringConfig } from '../ratingConfigs/ratingConfigs';


// ************************************************************************
// Farger
// ************************************************************************

// doc.setFillColor(85, 31, 47); // Rød header i KP, #FFFFFF
// doc.setTextColor(246, 234, 190); // Gul tekst i header, #F6EABE
// doc.setFillColor(200, 173, 127); // Gul knapp i KP, #c8ad7f
// doc.setFillColor(4, 79, 103); // Blå knapp i KP, #044F67
// doc.setTextColor(0, 0, 0); // Svart tekst osv, #000000
// doc.setTextColor(255, 255, 255); // Hvit, #FFFFFF

const privatPdfV3 = async (data, user, date, kreditor, debtorBirthNumber, localeMessages) => {
  
  // ************************************************************************
  // Hjelpere
  // ************************************************************************

  async function loadFontAsBase64(url) {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(arrayBuffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  const formatDate = (currentDate) => {
    if (!currentDate) return '';
    const day = currentDate.getDate().toString().padStart(2, "0");
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const year = currentDate.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatNumberWithSpaces = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const checkNumberValue = (num) => {
    if (num === '' || num === undefined || num === null || num === 0 || num === '0') {
      return '0';
    }
    return num.toString();
  }

  const checkString = (value) => {
    if (value === undefined || value === null || value === '') {
      return '-'
    }
    return value.toString();
  }

  const setDynamicFontSize = (wordWidth) => {
    if (!wordWidth) return 0;
  
    switch (true) {
      case wordWidth < 70:
        return 20;
      case wordWidth < 80:
        return 16;
      case wordWidth < 90:
        return 14;
      case wordWidth < 100:
        return 12;
      default:
        return 20;
    }
  };

  const createFullName = (first, last) => {
    const firstName = first?.trim() || "";
    const lastName = last?.trim() || "";
    if (!firstName && !lastName) return 'Fant ikke navn';
    return [firstName, lastName].filter(Boolean).join(" ");
  }

  function generateAutoTableSingleColumn(data, config, headerPrefix, addNæringskode = false) {

    // Lager headerRow
    const headerRow = [config.header, headerPrefix];
    
    // Lager body
    // Første celle er tittel og nr 2 er verdien
    const body = config.fields.map(field => {
        const value = data.hasOwnProperty(field.key) ? data[field.key] : '-';
        return [field.title, value];
      });
      
    // Legger til næringskode på slutten om ønskelig
    if (addNæringskode) {
        const title = næringConfig[1].fields[0].title;
        const value1 = data.hasOwnProperty(næringConfig[1].fields[0].key) ? checkString(data[næringConfig[1].fields[0].key]) : '';
        const value2 = data.hasOwnProperty(næringConfig[1].fields[1].key) ? checkString(data[næringConfig[1].fields[1].key]) : '';
        const extraValue = `${value1} ${value2}`;
        body.push([title, extraValue]);
    }
    
    return { head: [headerRow], body };
  }

  function generateAutoTableDoubleColumn(dataObject, config1, config2, headerPrefix) {

    // Lager headerRow, sender inn prefix, tom string i dette tilfellet, henter de to andre fra config.
    const headerRow = [headerPrefix, config1.header, config2.header];
  
    // Lager body, viktig at siden begge (config1 og config2) har samme rekkefølge i config siden de deler field titles.
    const body = config1.fields.map((field, index) => {
      const title = field.title;
      // Ser etter matchende key i dataObject
      const value1 = dataObject.hasOwnProperty(field.key) ? checkString(dataObject[field.key]) : '';
      // Bruker tilsvarende felt i config2
      const field2 = config2.fields[index];
      const value2 = dataObject.hasOwnProperty(field2.key) ? checkString(dataObject[field2.key]) : '';
      return [title, value1, value2];
    });
  
    return { head: [headerRow], body };
  }

  function generateAutoTableTripleColumn(dataObject, config1, config2, config3, headerPrefix) {
    // Lager headerRow, sender inn prefix og henter de tre andre fra config.
    const headerRow = [headerPrefix, config1.header, config2.header, config3.header];
    
    // Lager body, viktig at siden begge (config1 og config2) har samme rekkefølge i config siden de deler field titles.
    // Merk at config3 kun har de tre første verdiene. 
    const body = config1.fields.map((field, index) => {
      const title = field.title;
      const value1 = dataObject.hasOwnProperty(field.key) ? checkString(dataObject[field.key]) : '';
      
      // config2 har samme field.title som config1
      const field2 = config2.fields[index];
      const value2 = dataObject.hasOwnProperty(field2.key) ? checkString(dataObject[field2.key]) : '';
      
      // config3 har bare de tre første field.title (Se ratingConfig.js), må derfor sjekke på verdier
      let value3 = '';
      if (index < config3.fields.length) {
        const field3 = config3.fields[index];
        value3 = dataObject.hasOwnProperty(field3.key) ? checkString(dataObject[field3.key]) : '';
      }
      
      return [title, value1, value2, value3];
    });
    
    return { head: [headerRow], body };
  }

  function generateAutoTableLigninger(dataObject, config) {
    let items = dataObject.ligninger;
    if (!items) {
      items = [];
    } else if (!Array.isArray(items)) {
      items = [items];
    }

    // Lager subheaders basert på key i config (ligningsår)
    const subheaderLabels = items.map(item => item[config.subheaderKey]);
    
    // Lager headerrow
    const headerRow = [config.header, ...subheaderLabels];
    
    // Lager body
    const body = config.fields.map(field => {
      const row = [field.title];
      items.forEach(item => {
        row.push(item[field.key] !== undefined ? checkString(item[field.key]) : '');
      });
      return row;
    });
    
    return { head: [headerRow], body };
  }

// Brukes for aksjer og aksjonærer
function generateAutoTableDataForMultiBlock(dataArray, config, headerPrefix, spacerRowCount = 1) {
    // Lager blokker med data for hver aksjonær/aksje
    const blocks = dataArray.map(dataObj => {
      const result = generateAutoTableSingleColumn(dataObj, config, headerPrefix);
      return result.body; // Hver rad er [title, key]
    });
    
    const blockRowCount = config.fields.length;
    const combinedRows = [];
    
    // To blokker på hver rad
    for (let i = 0; i < blocks.length; i += 2) {
      const leftBlock = blocks[i];
      const rightBlock = (i + 1 < blocks.length)
        ? blocks[i + 1]
        : Array.from({ length: blockRowCount }, () => ['', '']);
      
      for (let rowIndex = 0; rowIndex < blockRowCount; rowIndex++) {
        const combinedRow = [...leftBlock[rowIndex], ...rightBlock[rowIndex]];
        combinedRows.push(combinedRow);
      }
      
      const numCells = 4;
      const emptyRow = new Array(numCells).fill('');
      for (let s = 0; s < spacerRowCount; s++) {
        combinedRows.push(emptyRow);
        combinedRows.push(emptyRow);
      }
    }

    // Placeholder om ingen data
    if (combinedRows.length === 0) {
        combinedRows.push(['Ingen resultat', '', '', '']);
    }
    
    return { head: [], body: combinedRows };
  }


  function generateAutoTableDataFromListValueSingle(data, config) {
    let items = data;
    if (!items) {
      items = [];
    } else if (!Array.isArray(items)) {
      items = [items];
    }
  
    const headerRow = config.fields.map(field => field.title);
  
    const body = items.map(item =>
      config.fields.map(field =>
        item.hasOwnProperty(field.key) ? checkString(item[field.key]) : ''
      )
    );
  
    return { head: [headerRow], body };
  }
    

  // ************************************************************************
  // Lag dokument
  // ************************************************************************
  
  // Lag nytt jsPDF dokument
  const doc = new jsPDF();

  // Funksjon for å sjekke om ny side må legges til
  const addNewPageIfNeeded = (doc, currentY, marginBottom = 10) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (currentY > pageHeight - marginBottom) {
      doc.addPage();
      return 20; // ny y-koordinat med litt margin
    }
    return currentY;
  };

  const getFileName = () => {
    const nameValue = data.navn && data.navn.trim() !== '' 
        ? data.navn 
        : '';
    
    return `Kredittvurdering ${nameValue}.pdf`;
  };


  // ************************************************************************
  // Fonter
  // ************************************************************************

  // Laster og registrerer alle fonter
  const boldBase64 = await loadFontAsBase64(FellixBoldFontUrl);
  doc.addFileToVFS('Fellix-Bold.ttf', boldBase64);
  doc.addFont('Fellix-Bold.ttf', 'Fellix', 'bold');

  const semiboldBase64 = await loadFontAsBase64(FellixSemiBoldFontUrl);
  doc.addFileToVFS('Fellix-SemiBold.ttf', semiboldBase64);
  doc.addFont('Fellix-SemiBold.ttf', 'Fellix', 'semiBold');

  const regularBase64 = await loadFontAsBase64(FellixRegularFontUrl);
  doc.addFileToVFS('Fellix-Regular.ttf', regularBase64);
  doc.addFont('Fellix-Regular.ttf', 'Fellix', 'normal');


  // ************************************************************************
  // Sidenummer
  // ************************************************************************

  function findPageNumber(doc, pageData) {
    
    doc.setFont("Fellix", "semiBold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
  
    const currentPage = doc.internal.getNumberOfPages();
    const totalPagesExp = '{total_pages_count_string}'; // Denne blir satt helt på slutten
    let pageStr = "Side " + currentPage;
    if (typeof doc.putTotalPages === 'function') {
        pageStr += " av " + totalPagesExp;
    }
    const pageSize = doc.internal.pageSize;
    const pageHeight = pageSize.height || pageSize.getHeight();
    // Sidenummer skal være nederst til venstre
    doc.text(pageStr, pageData.settings.margin.left, pageHeight - 10);
  }


  // ************************************************************************
  // Header
  // ************************************************************************

  // Bestemmer header høyde og bredden på pdf. (units, e.g., mm)
  const headerHeight = 30;
  const pageWidth = doc.internal.pageSize.getWidth();

  // Farger og lager header som Logo og bruker-tekst skal inn i
  doc.setFillColor(255, 255, 255); // Rød header i KP, #FFFFFF
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Gul, #F6EABE
  doc.setFontSize(10);

  // Header-teksten skal være på venstre side
  let currentY = 0; // Hjelper for å kunne holde styr på hvor vi er og om vi må legge til ny side
  const leftMargin = 10; // Margin venstre som tekst bruker
  const kredittvurderingY = 8; // y-koordinator for kredittvurdering linjen
  const kreditorY = 18; // y-koordinater for kreditor linjen
  const dateY = 13; // y-koordinater for dato linjen
  const userY = 23; // y-koordinater for bruker linjen
  const marginBottom = 10; // Brukes når vi sjekker om vi må legge til ny side
  const verticalSpacing = 12; // Vertikal spacing mellom tittel og verdi
  const pad = 5; // Padding for hver kolonne
  const marginLeft = 10;
  const marginRight = 10;
  const totalTableWidth = pageWidth - marginLeft - marginRight;


  // Sub header verdier
  const headerLeftMargin = 8; // Margin for sub headers
  const headerRightMargin = 8; // Margin for sub headers
  const subHeaderHeight = 12; // Høyde for sub headers
  const subHeaderWidth = pageWidth - headerLeftMargin - headerRightMargin; // Bredde for sub headers
  const cornerRadius = 3; // Avrundede hjørner for sub headers
  const subHeaderTekstX = headerLeftMargin + 3; // Teksten i sub headers skal ha en liten margin til venstre
  let subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Plasserer teksten vertikalt i sub headers, denne oppdateres underveis

  // Type kredittvurdering
  doc.setFont("Fellix", "normal");
  doc.text("Type: ", leftMargin, kredittvurderingY);
  const typeLabelWidth = doc.getTextWidth("Type: ");
  doc.setFont("Fellix", "semiBold");
  doc.text("Kredittvurdering", leftMargin + typeLabelWidth, kredittvurderingY);

  // Kreditor som har utført
  doc.setFont("Fellix", "normal");
  doc.text("Kreditor: ", leftMargin, kreditorY);
  const kreditorLabelWidth = doc.getTextWidth("Kreditor: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(checkString(kreditor), leftMargin + kreditorLabelWidth, kreditorY);

  // Dato utført
  doc.setFont("Fellix", "normal");
  doc.text("Dato utført: ", leftMargin, dateY);
  const dateLabelWidth = doc.getTextWidth("Dato utført: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(formatDate(date), leftMargin + dateLabelWidth, dateY);

  // Bruker som har utført
  doc.setFont("Fellix", "normal");
  doc.text("Utført av: ", leftMargin, userY);
  const userLabelWidth = doc.getTextWidth("Utført av: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(checkString(user), leftMargin + userLabelWidth, userY);

  // Logo
  const logoWidth = 60;  // Logo sin bredde i mm
  const logoHeight = 15; // Logo sin høyde i mm
  const logoX = pageWidth - logoWidth - 10; // Margin fra høyre kant
  const logoY = 5; // Sentrer i midten av header
  doc.addImage(KfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);


  // ************************************************************************
  // Person og score
  // ************************************************************************

  // Person
  currentY += 45; // y-koordinater for navn linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setFont("Fellix", "bold");
  doc.setTextColor(0, 0, 0); // Svart, #000000
  doc.setFontSize(22);
  doc.text(checkString(data.navn), leftMargin, currentY);

  currentY += 10; // y-koordinator for personnr og type tag
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Personnr og type tag skal være rett under navn
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(14);
  doc.text(debtorBirthNumber, leftMargin, currentY);

  const debtorBirthNumberWidth = doc.getTextWidth(debtorBirthNumber);
  // Kalkulerer bredden til tekst for sentrering i rektangel
  const typeWidth = doc.getTextWidth("Privat ");
  const typeRectangleWidth = typeWidth;
  const typeRectangleHeight = 6;
  const maxTextWidth = 50;

  // Rektangel sin x-posisjon skal være rett etter personnr + litt margin
  const rectangleX = leftMargin + debtorBirthNumberWidth + 4;

  // Setter svart border og line width for rektangel
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);

  // Lager rektangel for type med 1mm border radius
  doc.roundedRect(rectangleX, currentY - 5, typeRectangleWidth, typeRectangleHeight, 1, 1, 'S'); 
  
  const typeX = rectangleX + (typeRectangleWidth - typeWidth) / 2;

  // Add the text inside the rectangle
  doc.setFontSize(10);
  doc.text("Privat", typeX + 2.5, currentY - 0.7);

  currentY += 15; // y-koordinater for generell info linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  const scoreSpacing = 10;

   // Kalkulerer bredde for alle kolonnene
  // Kolonne 1: Rating
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col1LabelWidth = doc.getTextWidth(localeMessages['gradeLabel']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const col1ValueWidth = doc.getTextWidth(String(data.scoreKarakter)) + pad;
  const col1Width = Math.max(col1LabelWidth, col1ValueWidth);

  // Kolonne 2: Score
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col2LabelWidth = doc.getTextWidth(localeMessages['scoreLabel']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const col2ValueWidth = doc.getTextWidth(String(data.score)) + pad;
  const col2Width = Math.max(col2LabelWidth, col2ValueWidth);

  // Kolonne 3: Vurdering
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col3LabelWidth = doc.getTextWidth(localeMessages['ratingLabel']) + pad;
  doc.setFont("Fellix", "normal");
  doc.setFontSize(20);
  const col3ValueWidth = doc.getTextWidth(data.scoreForklaring) + pad;
  const col3Width = Math.max(col3LabelWidth, col3ValueWidth);
  const reduserVurderingWidth = col3ValueWidth > 60;

  // Kalkulerer x-posisjoner basert på bredde
  const scoreCol1X = leftMargin;
  const scoreCol2X = scoreCol1X + col1Width + scoreSpacing;
  const scoreCol3X = scoreCol2X + col2Width + scoreSpacing;
  const scoreCol4X = reduserVurderingWidth ? (scoreCol3X - 13) + col3Width + scoreSpacing : scoreCol3X + col3Width + scoreSpacing;

  // Kolonne 1: Rating
  doc.setTextColor(0, 0, 0);
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['gradeLabel'], scoreCol1X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(checkString(data.scoreKarakter), scoreCol1X, currentY + verticalSpacing);

  // Kolonne 2: Score
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['scoreLabel'], scoreCol2X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(checkNumberValue(data.score), scoreCol2X, currentY + verticalSpacing);

  // Kolonne 3: Vurdering
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['ratingLabel'], scoreCol3X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(setDynamicFontSize(col3ValueWidth)); // Justerer fontsize basert på vurdering sin lengde
  doc.text(checkString(data.scoreForklaring), scoreCol3X, currentY + verticalSpacing);

  // Kolonne 4: Limit
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['recommended_limitLabel'], scoreCol4X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.kredittgrense), scoreCol4X, currentY + verticalSpacing);  



  // ************************************************************************
  // Personinformasjon
  // ************************************************************************
  
  // Generell informasjon får sin egen side siden det er mye informasjon
  currentY += 30;

  // Farger og lager subheader
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Person", subHeaderTekstX, subHeaderTekstY);

  currentY += 20;

  const personConfig = privatConfig[0];

  const personData = generateAutoTableSingleColumn(data, personConfig, '');

  autoTable(doc, {
    head: personData.head,
    body: personData.body,
    theme: 'plain',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 140, fontStyle: 'bold' },
    },
    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
  });


  // ************************************************************************
  // Adresse
  // ************************************************************************
  
  currentY += 65;
    
  const bostedsAdrConfig = privatConfig[1];
  const postAdrConfig = privatConfig[2];
  const postboksConfig = privatConfig[3];

  const adresseTableData = generateAutoTableTripleColumn(data, bostedsAdrConfig, postAdrConfig, postboksConfig, 'Adresser');


  autoTable(doc, {
    head: adresseTableData.head,
    body: adresseTableData.body,
    theme: 'plain',
    styles: {
        font: "Fellix",
        fontSize: 10,
    },
    headStyles: {
        fontStyle: 'bold',
        fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 55, fontStyle: 'bold' },
        2: { cellWidth: 55, fontStyle: 'bold' },
        3: { cellWidth: 50, fontStyle: 'bold' },
    },
    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: 0 },
    startY: currentY,
    didDrawPage: function (pageData) {
        findPageNumber(doc, pageData);
      }
  });



  // ************************************************************************
  // Oversikt -> ny side
  // ************************************************************************
 
  // Høyde for Oversikt header
  doc.addPage(); 
  currentY = 20;

  // Farger og lager sub header
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Oversikt", subHeaderTekstX, subHeaderTekstY); 

  currentY += 20; // Litt whitespace mellom subheader og table under

  doc.setTextColor(0, 0, 0);

  // Status
  const tvangsConfig = privatConfig[5];
  const tvangsData = generateAutoTableSingleColumn(data, tvangsConfig, '');

  autoTable(doc, {
    head: tvangsData.head,
    body: tvangsData.body,
    theme: 'plain',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 60, fontStyle: 'bold' },
    },

    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
  });

  // Pant skal være til høyre for Status, finner x-posisjon
  const gap = 10;
  const secondColumnX = marginLeft + 100 + gap;

  // Pant
  const gjeldsConfig = privatConfig[7];
  const gjeldsData = generateAutoTableSingleColumn(data, gjeldsConfig, '');

  autoTable(doc, {
    head: gjeldsData.head,
    body: gjeldsData.body,
    theme: 'plain',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 50, fontStyle: 'bold' },
    },
    // startX: 90,
    tableWidth: totalTableWidth,
    margin: { left: secondColumnX, right: marginRight },
    startY: currentY,
  });

  currentY += 40;

  // Tvangsforretning
  const pantConfig = privatConfig[4];
  const pantData = generateAutoTableSingleColumn(data, pantConfig, '');

  autoTable(doc, {
    head: pantData.head,
    body: pantData.body,
    theme: 'plain',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 60, fontStyle: 'bold' },
    },
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    startY: currentY,
  });


  // Arrest
  const arrestConfig = privatConfig[6];
  const arrestData = generateAutoTableSingleColumn(data, arrestConfig, '');

  autoTable(doc, {
    head: arrestData.head,
    body: arrestData.body,
    theme: 'plain',
    styles: {
      font: "Fellix",
      fontSize: 10,
    },
    headStyles: {
      fontStyle: 'bold',
      fontSize: 12,
    },
    columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 50, fontStyle: 'bold' },
    },
    // startX: 90,
    tableWidth: totalTableWidth,
    margin: { left: secondColumnX, right: marginRight },
    startY: currentY,
  });

  // ************************************************************************
  // Roller
  // ************************************************************************
 
  // Høyde for Roller sub header
  currentY += 45; // Ny side om >= 300
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Farger og lager header
  doc.setFillColor(255, 255, 255); // Gul knapp i KP, #c8ad7f
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Svart, #000000
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Roller", subHeaderTekstX, subHeaderTekstY);

  currentY += 20; // Vertikal posisjon for Roller-verdiene

  const rollerData = generateAutoTableDataFromListValueSingle(data.roller, expandablePrivatConfig.roller);

  autoTable(doc, {
    head: rollerData.head,
    body: rollerData.body,
    theme: 'plain',
    styles: {
        font: "Fellix",
        fontStyle: 'bold',
        fontSize: 10,
    },
    headStyles: {
        fontStyle: 'normal',
        fontSize: 10,
    },
    startY: currentY,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    didDrawPage: function (pageData) {
        findPageNumber(doc, pageData);
      }
  });

  // ************************************************************************
  // Ligninger
  // ************************************************************************

  // Regnskap får sin egen side siden det er mye informasjon
  doc.addPage(); 
  currentY = 20;

  // Farger og lager subheader
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Ligninger", subHeaderTekstX, subHeaderTekstY);

  currentY += 20;
  
  // Genererer dataen basert på kredittvurderingsdataen og config
  const ligningerData = generateAutoTableLigninger(data, expandablePrivatConfig.ligninger);

  autoTable(doc, {
    head: ligningerData.head,
    body: ligningerData.body,
    theme: 'plain',
    styles: {
        font: "Fellix",
        fontSize: 10,
    },
    headStyles: {
        fontStyle: 'bold',
        fontSize: 12,
    },
    // Setter bredde slik at alle kolonner er på linje vertikalt
    columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 45, fontStyle: 'bold' },
        2: { cellWidth: 45, fontStyle: 'bold' },
        3: { fontStyle: 'bold' },
    },
    startY: currentY,
    startX: marginLeft,
    tableWidth: totalTableWidth,
    margin: { left: marginLeft, right: marginRight },
    didDrawPage: function (pageData) {
        findPageNumber(doc, pageData);
        }
  });
  currentY = doc.lastAutoTable.finalY + 5;


  // ************************************************************************
  // Betalingsanmerkninger
  // ************************************************************************

  // Betalingsanmerkninger skal være under ligninger
  currentY += 10;

  // Farger og lager subheader
  doc.setFillColor(255, 255, 255); // Blå knapp i KP, #044F67
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Hvit, #FFFFFF
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Betalingsanmerkninger", subHeaderTekstX, subHeaderTekstY);

  currentY += 20; // Vertikal posisjon for Betalingsanmerkninger-verdiene
  
  const betalingsanmerkningerData = generateAutoTableDataForMultiBlock(data.betalingsanmerkninger, expandablePrivatConfig.betalingsanmerkninger, '', 1);

  autoTable(doc, {
    head: betalingsanmerkningerData.head,
    body: betalingsanmerkningerData.body,
    theme: 'plain',
    styles: {
        font: "Fellix",
        fontSize: 10,
        cellPadding: 1,
      },
      headStyles: {
        fontStyle: 'bold',
        fontSize: 12,
        cellPadding: 5,
      },
      // Adjust column widths as needed; here we assume four columns.
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 60, fontStyle: 'bold' },
        2: { cellWidth: 40 },
        3: { cellWidth: 60, fontStyle: 'bold' },
      },
      startY: currentY,
      tableWidth: 180,
      margin: { left: 10, right: 8 },
    });


  // ************************************************************************
  // Aksjer
  // ************************************************************************
 
  // Lager ny side siden Aksjer kan være veldig lang
  doc.addPage();
  currentY = 20;

  // Farger og lager subheader
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0);  // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Aksjer", subHeaderTekstX, subHeaderTekstY);

  currentY += 20; // Vertikal posisjon for Aksjer-verdiene

  const aksjerData = generateAutoTableDataForMultiBlock(data.aksjer, expandablePrivatConfig.aksjer, '', 1);

  autoTable(doc, {
    head: aksjerData.head,
    body: aksjerData.body,
    theme: 'plain',
    styles: {
        font: "Fellix",
        fontSize: 10,
        cellPadding: 1,
      },
      headStyles: {
        fontStyle: 'bold',
        fontSize: 12,
        cellPadding: 5,
      },
      // Adjust column widths as needed; here we assume four columns.
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 60, fontStyle: 'bold' },
        2: { cellWidth: 45 },
        3: { cellWidth: 60, fontStyle: 'bold' },
      },
      startY: currentY,
      tableWidth: 180,
      margin: { left: 10, right: 8 },
      didDrawPage: function (pageData) {
        findPageNumber(doc, pageData);
      }
    });


    // Setter total antall sider helt til slutt
    if (typeof doc.putTotalPages === 'function') {
        doc.putTotalPages('{total_pages_count_string}');
    }


  return doc.output('blob')

};

export default privatPdfV3;
